// Recharts chart click event data (from onClick handlers on chart components)
export interface RechartsClickData {
  activePayload?: Array<{
    payload?: Record<string, unknown>
    dataKey?: string
    value?: number
    name?: string
    [key: string]: unknown
  }>
  activeLabel?: string
  [key: string]: unknown
}

export interface CustomerRanking {
  customer_id: number
  customer_name: string
  value: number
  target?: number
  trend?: 'up' | 'down' | 'stable'
  percentage_change?: number
}

export interface MetricDelta {
  current: number
  previous: number
  change: number
  percentage_change: number
  trend: 'up' | 'down' | 'stable'
}

export interface DashboardData {
  // Company overview
  companies: {
    total: number
    suppliers: number
    customers: number
  }
  // Sites overview
  sites: {
    total: number
  }
  // Users overview
  users: {
    total: number
    pendingApprovals: number
  }
  // Purchasing metrics
  purchasing: {
    totalPOs: number
    openPOs: number
    totalLines: number
    byStatus: Record<string, number>
    totalValue: number
  }
  // Quality metrics
  // CR-4641 round 2: ppm and otif are nullable when the CustomerProfile
  // include flag excludes all matching customers (backend returns null;
  // frontend hides the chart card and disables drill-down).
  quality: {
    ppm: number | null
    ppmTarget: number
    ncrs: number
    ncrsByStatus: Record<string, number>
    ncrsBySeverity: Record<string, number>
    otif: number | null
    otifTarget: number
  }
  // Delivery metrics
  delivery: {
    otd: number | null
    otdTarget: number
    appeals: number
    appealsBreakdown?: {
      raised_by_supplier: number
      raised_by_customer: number
      accepted: number
      rejected: number
      auto_approved: number
    }
    openOrderValue: number
  }
  // Customer metrics
  customers: {
    total: number
    active: number
    preferred: number
    avgQuality: number
    avgDelivery: number
  }
  // Cost metrics
  cost: {
    totalSpend: number
    historicalArrears: number
  }
  // NEW: Customer rankings for actionable insights
  customerRankings?: {
    worstPPM: CustomerRanking[]
    worstOTIF: CustomerRanking[]
    worstOTD: CustomerRanking[]
    highestArrears: CustomerRanking[]
  }
  // NEW: Month-over-month deltas for KPIs (Phase 5)
  deltas?: {
    ppm: MetricDelta
    otif: MetricDelta
    otd: MetricDelta
    ncrs: MetricDelta
    open_order_value: MetricDelta
    total_spend: MetricDelta
    arrears_value: MetricDelta
  }
  // CR-4211: Configurable targets from CustomerPerformanceSettings
  targets?: {
    ppm: number
    otif: number
    otd: number
  }
  // Delivery metric mode: which metrics are active
  delivery_metric_mode?: 'both' | 'otif_only' | 'otd_only'
  // CR-5257 + CR-5258 — operational status snapshot
  operational?: {
    wip: { count: number; value: number }
    on_hold: { count: number; value: number }
    overs_15d: { count: number; value: number } | null
    priority: { count: number; value: number } | null
  }
  costData?: {
    total_incoming_value: number
    total_incoming_lines: number
    total_arrears_value: number
    total_arrears_lines: number
    total_customers: number
    incoming_by_week: Array<{
      week: string
      value: number
    }>
    arrears_by_customer: Array<{
      customer: string
      arrears: number
      count: number
    }>
    incoming_lines?: Array<{
      po_number: string
      customer: string
      part_number: string
      qty: number
      unit_price: number
      total_value: number
      delivery_date: string
    }>
    arrears_lines?: Array<{
      po_number: string
      customer: string
      part_number: string
      qty_outstanding: number
      days_overdue: number
      value: number
      due_date: string
    }>
  }
  siteKPI: {
    otifBySite: Array<{
      site: string
      otif: number
      target: number
      orders: number
    }>
    ppmBySite: Array<{
      site: string
      ppm: number
      target: number
      parts: number
    }>
    ncrBreakdown: Array<{
      month: string
      open: number
      closed: number
      overdue: number
    }>
    deliveryBySite: Array<{
      site: string
      onTime: number
      early: number
      late: number
    }>
  }
  groupKpi?: {
    internal: {
      month_otif: number
      fytd_otif: number
      month_otd: number
      fytd_otd: number
      month_ppm: number
      fytd_ppm: number
      otif_trend: Array<{
        month: string
        otif: number
        target: number
      }>
      otd_trend: Array<{
        month: string
        otd: number
        target: number
      }>
      ppm_trend: Array<{
        month: string
        ppm: number
        target: number
      }>
      total_spend: number
      components_delivered: number
      components_rejected: number
      arrears_value: number
      arrears_count: number
    } | null
    external: {
      month_otif: number
      fytd_otif: number
      month_otd: number
      fytd_otd: number
      month_ppm: number
      fytd_ppm: number
      otif_trend: Array<{
        month: string
        otif: number
        target: number
      }>
      otd_trend: Array<{
        month: string
        otd: number
        target: number
      }>
      ppm_trend: Array<{
        month: string
        ppm: number
        target: number
      }>
      total_spend: number
      components_delivered: number
      components_rejected: number
      arrears_value: number
      arrears_count: number
    } | null
  }
  charts: {
    ppmTrend: Array<{
      month: string
      ppm: number
      target: number
    }>
    otifData: Array<{
      month: string
      otif: number
      target: number
      otifCount: number
      arrears: number
      failed: number
    }>
    spendByMonth: Array<{
      month: string
      spend: number
      lines?: number
    }>
    otdData: Array<{
      month: string
      otd: number
      target: number
      onTime: number
      early: number
      late: number
    }>
    liveNCRsOverdue: Array<{
      month: string
      overdue: number
      pending: number
    }>
    historicalArrears: Array<{
      month: string
      value: number
      count?: number
    }>
    arrearsBacklog?: Array<{
      week: string
      count: number
      value: number
    }>
    milestones?: Array<{
      title: string
      date: string
    }>
    dateAdherenceData?: Array<{
      customer: string
      month: string
      total: number
      onTime: number
      adherence: number
    }>
    appealsTrend?: Array<{
      month: string
      awaitingSupplier: number
      accepted: number
      awaitingCustomer: number
      rejected: number
    }>
    ppmNCRs?: Array<{
      ncr_id: string
      supplier_ncr_number: string
      supplier: string
      customer: string
      part_number: string
      qty_rejected: number
      reject_value: number
      date_raised: string
      liability: string
      status: string
      time_taken: number
    }>
    ppmGRNs?: Array<{
      grn_number: string
      supplier: string
      customer: string
      po_number: string
      part_number: string
      qty: number
      delivery_date: string
    }>
    otifLines?: Array<{
      po_number: string
      customer: string
      part_number: string
      qty: number
      delivery_date: string
      otif_status: string
    }>
    otifPOL?: Array<{
      po_number: string
      po_line: string
      customer: string
      part_number: string
      qty_ordered: number
      qty_outstanding: number
      delivery_date: string
    }>
    otifArrears?: Array<{
      po_number: string
      customer: string
      part_number: string
      qty_outstanding: number
      days_overdue: number
      value: number
    }>
    otifFailed?: Array<{
      po_number: string
      customer: string
      part_number: string
      qty: number
      planned_date: string
      actual_date: string
      days_late: number
    }>
    otdOntime?: Array<{
      po_number: string
      customer: string
      part_number: string
      qty: number
      planned_date: string
      actual_date: string
      days_diff: number
      status: string
    }>
    otdEarly?: Array<{
      po_number: string
      customer: string
      part_number: string
      qty: number
      planned_date: string
      actual_date: string
      days_diff: number
      status: string
    }>
    otdLate?: Array<{
      po_number: string
      customer: string
      part_number: string
      qty: number
      planned_date: string
      actual_date: string
      days_diff: number
      status: string
    }>
    otdAll?: Array<{
      po_number: string
      customer: string
      part_number: string
      qty: number
      planned_date: string
      actual_date: string
      days_diff: number
      status: string
    }>
    spendLines?: Array<{
      po_number: string
      customer: string
      part_number: string
      qty: number
      unit_price: number
      total_value: number
      order_date: string
    }>
    arrearsDetails?: Array<{
      po_number: string
      customer: string
      part_number: string
      qty_outstanding: number
      days_overdue: number
      value: number
      due_date: string
    }>
    ncrDetails?: Array<{
      ncr_id: string
      supplier_ncr_number: string
      customer: string
      part_number: string
      qty_rejected: number
      date_raised: string
      due_date: string
      days_overdue: number
      status: string
    }>
  }
}

export interface LoadingStates {
  core: boolean
  purchasing: boolean
  quality: boolean
  customers: boolean
}

export interface DashboardFilters {
  dateFrom: string
  dateTo: string
  customer: string
  supplier: string
  site: string
  currency: string
}

export interface TabProps {
  data: DashboardData
  currency: {
    currentCurrency: string
  }
  filters?: DashboardFilterState
  onDrillDown?: (type: string, data?: Record<string, unknown>) => void
  loadingStates?: LoadingStates
  // CR-3930: Weekly/Monthly toggle
  dataPeriod?: 'weekly' | 'monthly'
  // CR-3931: Orders toggle
  orderType?: 'all' | 'scheduled' | 'spot'
}

// ============================================================================
// Saved Filter Types
// ============================================================================

export interface SavedDashboardFilter {
  id: number
  name: string
  description?: string
  date_from?: string | null
  date_to?: string | null
  customer?: number | null
  customer_name?: string
  supplier?: number | null
  supplier_name?: string
  site?: number | null
  site_name?: string
  currency: string
  customer_group?: number | null
  customer_group_name?: string
  strategic_only?: boolean
  customer_type?: string
  commodity_group?: number | null
  commodity_type?: number | null
  is_shared: boolean
  is_default: boolean
  created_by: number
  created_by_name?: string
  created_at: string
  updated_at?: string
}

// ============================================================================
// Extended Filter State (includes all filter options)
// ============================================================================

export interface DashboardFilterState {
  dateFrom: string
  dateTo: string
  customer: string
  supplier: string
  site: string
  currency: string
  customerGroup: string
  strategicOnly: boolean
  customerType: string
  commodityGroup: string
  commodityType: string
  // CR-3932 to CR-3937: Additional filters
  siteCountry: string
  customerCountry: string
  customerCode: string
  orderType: string
  warehouse: string
  dataMode: 'historical' | 'live'
  // CR-4349: Division filter (conditional on org structure)
  division: string
  // CR-4800: Compliance-page-only filters
  approvalStage: string
  riskCategory: string
}

// ============================================================================
// Toast State
// ============================================================================

export interface ToastState {
  message: string
  type: 'success' | 'error'
  visible: boolean
}

// ============================================================================
// Tab Definition
// ============================================================================

export interface DashboardTab {
  id: string
  label: string
  active?: boolean
}

/** Default tab when opening the Dashboard page. */
export const DEFAULT_DASHBOARD_TAB_ID = 'sales-orders';

/** @deprecated Use DEFAULT_DASHBOARD_TAB_ID */
export const OPERATING_ORG_DEFAULT_DASHBOARD_TAB_ID = DEFAULT_DASHBOARD_TAB_ID;

/** Tabs controlled via Platform Admin → Dashboard Visibility. */
export const CONFIGURABLE_DASHBOARD_TAB_IDS = new Set([
  'sales-orders',
  'forecast-forward',
]);

/** Supplier-column defaults when a key is absent from saved platform settings. */
export const SUPPLIER_DASHBOARD_TAB_DEFAULTS: Record<string, boolean> = {
  'sales-orders': true,
  'forecast-forward': true,
};

/** Customer-column defaults when a key is absent from saved platform settings. */
export const CUSTOMER_DASHBOARD_TAB_DEFAULTS: Record<string, boolean> = {
  'sales-orders': true,
  'forecast-forward': false,
};

/** @deprecated Use CONFIGURABLE_DASHBOARD_TAB_IDS */
export const OPERATING_ORG_DASHBOARD_TAB_IDS = CONFIGURABLE_DASHBOARD_TAB_IDS;

/**
 * Single source of truth for all dashboard tabs.
 * Used by Dashboard.tsx for rendering. Admin settings use CONFIGURABLE_DASHBOARD_TABS only.
 */
export const DASHBOARD_TAB_REGISTRY: Array<{
  id: string
  label: string
  description: string
}> = [
  { id: 'default', label: 'Default', description: 'Overview dashboard with key metrics, KPIs, and summary charts' },
  { id: 'scheduled', label: 'Scheduled Dashboards', description: 'Live firm-window vs forecast schedules — qty, value, and coverage by part schedule_firm days (scheduling-agreement orders only)' },
  { id: 'spot-orders', label: 'Spot Orders', description: 'Spot / one-off orders — same KPI lens as Scheduled Dashboards but scoped to ERP order type 1' },
  { id: 'sales-orders', label: 'Sales Dashboard', description: 'Sales order OTD, deliveries, cost and status — current week through YTD with drill-down' },
  { id: 'forecast-forward', label: 'Forecast Forward', description: 'Open sales-order lines bucketed by promised delivery date into the next 4 weeks — expected quantity, value and order counts per week' },
  { id: 'cached-performance', label: 'Cached Performance', description: 'Point-in-time KPI snapshots captured nightly (frozen, will not shift as live data changes)' },
  { id: 'group-kpi', label: 'Group KPI', description: 'Group-level KPI analytics with internal/external performance' },
  { id: 'group-detail-kpi', label: 'Group Detail KPI', description: 'Detailed group-level KPI breakdown and drill-down data' },
  { id: 'site-kpi', label: 'Company KPI', description: 'Company-level OTIF, PPM, delivery depth, and spend analytics' },
  { id: 'cost', label: 'Cost', description: 'Incoming order value, arrears analysis, and spend tracking' },
  { id: 'emissions', label: 'Emissions', description: 'Carbon emissions tracking by transport mode and supplier' },
  { id: 'compliance', label: 'Compliance', description: 'Supplier approval status, risk categories, and questionnaire tracking' },
  { id: 'future-forecast', label: 'Future Forecast', description: 'Forward-looking delivery and demand forecasting' },
  { id: 'date-adherence', label: 'Date Adherence', description: 'Supplier delivery date adherence metrics and trends' },
  { id: 'expediting', label: 'Expediting', description: 'Order expediting management and overdue tracking' },
  { id: 'performance-ratings', label: 'Performance Ratings', description: 'Supplier performance rating and scoring overview' },
]

/** Admin-configurable subset — Platform Admin → Dashboard Visibility. */
export const CONFIGURABLE_DASHBOARD_TABS = DASHBOARD_TAB_REGISTRY.filter((tab) =>
  CONFIGURABLE_DASHBOARD_TAB_IDS.has(tab.id),
);

// ============================================================================
// Commodity Types
// ============================================================================

export interface CommodityGroup {
  id: number
  name: string
  code?: string
  description?: string
  is_active: boolean
}

export interface CommodityType {
  id: number
  commodity_group: number
  commodity_group_name?: string
  name: string
  code?: string
  description?: string
  is_active: boolean
}

// ============================================================================
// Emissions Data Types
// ============================================================================

export interface EmissionsData {
  total_carbon_kg: number
  by_transport: {
    road: { emissions_kg: number; distance_km: number; percentage: number }
    rail: { emissions_kg: number; distance_km: number; percentage: number }
    air: { emissions_kg: number; distance_km: number; percentage: number }
    sea: { emissions_kg: number; distance_km: number; percentage: number }
  }
  by_month: Array<{
    month: string
    road: number
    rail: number
    air: number
    sea: number
    total: number
  }>
  by_customer: Array<{
    customer: string
    emissions_kg: number
    percentage: number
  }>
  trend: Array<{
    month: string
    emissions: number
    trend_line: number
  }>
}

// ============================================================================
// Site KPI Data Types (Reference Design)
// ============================================================================

export interface SiteKPIData {
  otif: {
    month: number
    three_month: number
    twelve_month: number
  }
  appeals: {
    raised_by_supplier: number
    raised_by_customer: number
    accepted: number
    rejected: number
    auto_approved: number
  }
  spend: {
    month: number
    three_month: number
    twelve_month: number
  }
  otif_trend: Array<{
    month: string
    otif: number
    target: number
  }>
  appeals_trend: Array<{
    month: string
    awaiting_supplier: number
    accepted: number
    awaiting_customer: number
    rejected: number
  }>
  spend_trend: Array<{
    month: string
    spend: number
  }>
  depth_of_delivery: Array<{
    month: string
    on_time: number
    early: number
    late: number
  }>
  arrears_by_lines: Array<{
    month: string
    lines: number
  }>
  arrears_by_value: Array<{
    month: string
    value: number
  }>
}

// ============================================================================
// Compliance Data Types
// ============================================================================

export interface ComplianceData {
  live_contracts: number
  docs_awaiting_read: number
  customers_by_approval: Array<{
    status: string
    count: number
    percentage: number
    color: string
  }>
  customers_by_risk: Array<{
    category: string
    count: number
    percentage: number
    color: string
  }>
  questionnaires_by_status: Array<{
    status: string
    count: number
    percentage: number
    color: string
  }>
  contracts_by_auto_renew: Array<{
    type: string
    count: number
    percentage: number
    color: string
  }>
}
